import React from "react";
import { Crown } from "lucide-react";
import { ordinal } from "@/lib/ordinal";
import { METALLIC_SHEEN_CLASS, withBevel } from "@/lib/metallic";
import { MetallicSheen } from "@/components/common/MetallicSheen";

// Tier visual treatment. Member = Bright Blue #2575FC (corrected from indigo).
// TIER_STYLES is exported so the Avatar rank ring draws from the exact same hex.
export const TIER_STYLES = {
  founding: { bg: "linear-gradient(135deg, #F5D77A, #D4AF37)", color: "#3A2E05", ring: "rgba(212,175,55,0.55)", label: "Founding Member", crown: true },
  pioneer: { bg: "linear-gradient(135deg, #E8E8EC, #B8BCC4)", color: "#2A2D33", ring: "rgba(184,188,196,0.55)", label: "Pioneer", crown: false },
  charter: { bg: "linear-gradient(135deg, #D89A6A, #CD7F32)", color: "#3A2410", ring: "rgba(205,127,50,0.55)", label: "Charter", crown: false },
  member: { bg: "#2575FC", color: "#ffffff", ring: "rgba(37,117,252,0.5)", label: null, crown: false },
};

const SIZES = {
  sm: { pad: "1px 7px", font: 11, icon: 10, gap: 3 },
  md: { pad: "2px 10px", font: 13, icon: 13, gap: 4 },
  lg: { pad: "4px 14px", font: 15, icon: 16, gap: 5 },
};

// Reusable position pill. Usable anywhere a user appears: profile, comments,
// feed, chat, DMs, search, notifications.
export const SubscriberBadge = ({ position, tier, size = "md", showLabel = false, className = "", testid = "subscriber-badge" }) => {
  if (position === null || position === undefined) return null;
  const t = TIER_STYLES[tier] || TIER_STYLES.member;
  const s = SIZES[size] || SIZES.md;
  // Metallic treatment applies only to the blue accent (member) pill. The gold
  // Founding / silver Pioneer / bronze Charter tiers keep their own fills.
  const isAccent = (TIER_STYLES[tier] || TIER_STYLES.member) === TIER_STYLES.member;

  return (
    <span className={`inline-flex items-center ${className}`} style={{ gap: 6 }} data-testid={testid}>
      <span
        className={`inline-flex items-center font-semibold rounded-full whitespace-nowrap${isAccent ? ` ${METALLIC_SHEEN_CLASS}` : ""}`}
        style={{
          ...(isAccent ? {} : { background: t.bg }),
          color: t.color,
          padding: s.pad,
          fontSize: s.font,
          gap: s.gap,
          boxShadow: isAccent ? withBevel(`0 0 0 1px ${t.ring}`) : `0 0 0 1px ${t.ring}`,
          lineHeight: 1.2,
          position: "relative",
          overflow: "hidden",
        }}
        data-testid={`${testid}-pill`}
      >
        {/* Gold / silver / bronze keep their gradient fill; the sheen composites on
            top. member (accent) already carries the full metallic-sheen fill. */}
        {!isAccent && <MetallicSheen radius="9999px" />}
        {t.crown && <Crown size={s.icon} style={{ fill: "#3A2E05", position: "relative" }} />}
        <span style={{ position: "relative" }}>{ordinal(position)}</span>
      </span>
      {showLabel && t.label && (
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--text-secondary)" }} data-testid={`${testid}-label`}>
          {t.label}
        </span>
      )}
    </span>
  );
};

export default SubscriberBadge;
